import { BeLinkHttp } from '@be-link/http';
import {
	Button,
	Card,
	message,
	Radio,
	Select,
	Space,
	Spin,
	Typography,
} from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';
import type { IDomainUsedResponse } from '@/api/domain';

const { Text } = Typography;

// 环境API地址配置
const ENV_API_URLS: Record<'test' | 'production', string> = {
	test: 'https://ecommerce-dev.wejourney.top',
	production: 'https://ecommerce-release.wejourney.top',
};

// 创建独立的 HTTP 实例（不使用全局单例）
const prodHttp = new BeLinkHttp();
const testHttp = new BeLinkHttp();

// 初始化两个实例
prodHttp.init({ baseURL: ENV_API_URLS.production });
testHttp.init({ baseURL: ENV_API_URLS.test });

// 根据环境获取对应的 HTTP 实例
const getHttpInstance = (env: 'test' | 'production') => {
	return env === 'production' ? prodHttp : testHttp;
};

/**
 * 获取域名配置
 */
const fetchDomainUsed = async (env: 'test' | 'production') => {
	const http = getHttpInstance(env);
	return http.post<IDomainUsedResponse>('/c-bff/domain/used', {});
};

// 环境选项
const ENV_OPTIONS = [
	{ label: '生产环境', value: 'production' },
	{ label: '测试环境', value: 'test' },
];

// H5页面配置
const H5_PAGES = [
	{
		value: '/service-center',
		label: '个人中心',
		path: '/service-center',
	},
	{
		value: '/points-mall',
		label: '积分商城',
		path: '/points-mall',
	},
	{
		value: '/order-list',
		label: '订单列表',
		path: '/order-list',
	},
	{
		value: '/coupon',
		label: '优惠券列表',
		path: '/coupon',
	},
];

export default function EcommerceQrcode() {
	const [env, setEnv] = useState<'test' | 'production'>('production');
	const [selectedPage, setSelectedPage] = useState<string | undefined>();
	const [baseUrl, setBaseUrl] = useState<string>('');
	const [loading, setLoading] = useState(false);
	const qrCodeRef = useRef<HTMLDivElement>(null);

	// 获取域名（环境变化时重新获取）
	useEffect(() => {
		const currentEnv = env; // 捕获当前环境值
		const loadDomain = async () => {
			setLoading(true);
			try {
				console.log(
					`正在获取${currentEnv === 'test' ? '测试' : '生产'}环境域名...`,
				);
				const res = await fetchDomainUsed(currentEnv);
				// 使用重定向域名 (3)
				setBaseUrl(res.data.used['3'] || '');
			} catch (error) {
				message.error('获取域名失败');
				console.error('获取域名失败:', error);
			} finally {
				setLoading(false);
			}
		};

		loadDomain();
	}, [env]);

	// 生成完整URL
	const getFullUrl = () => {
		if (!selectedPage || !baseUrl) return '';
		const page = H5_PAGES.find((p) => p.value === selectedPage);
		return `${baseUrl}${page?.path || ''}`;
	};

	// 获取当前页面名称
	const getPageName = () => {
		if (!selectedPage) return '';
		const page = H5_PAGES.find((p) => p.value === selectedPage);
		return page?.label || '';
	};

	// 获取格式化的日期
	const getFormattedDate = () => {
		const now = new Date();
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, '0');
		const day = String(now.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	// 下载二维码
	const handleDownload = () => {
		if (!qrCodeRef.current) return;

		const svg = qrCodeRef.current.querySelector('svg');
		if (!svg) return;

		const svgData = new XMLSerializer().serializeToString(svg);
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		const img = new Image();

		// 文字内容
		const pageName = getPageName();
		const dateStr = getFormattedDate();
		const text = `${pageName} ${dateStr}`;

		img.onload = () => {
			if (!ctx) return;

			const scale = 2;
			const qrSize = img.width;
			const textHeight = 30; // 文字区域高度
			const padding = 10; // 文字与二维码的间距

			canvas.width = qrSize * scale;
			canvas.height = (qrSize + textHeight + padding) * scale;
			ctx.scale(scale, scale);

			// 绘制白色背景
			ctx.fillStyle = '#ffffff';
			ctx.fillRect(0, 0, canvas.width / scale, canvas.height / scale);

			// 绘制二维码
			ctx.drawImage(img, 0, 0);

			// 绘制文字
			ctx.fillStyle = '#333333';
			ctx.font =
				'14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
			ctx.textAlign = 'center';
			ctx.textBaseline = 'top';
			ctx.fillText(text, qrSize / 2, qrSize + padding);

			const pngFile = canvas.toDataURL('image/png');
			const downloadLink = document.createElement('a');
			downloadLink.download = `qrcode-${selectedPage}.png`;
			downloadLink.href = pngFile;
			downloadLink.click();
			message.success('二维码下载成功');
		};

		img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
	};

	// 复制链接
	const handleCopyUrl = () => {
		const url = getFullUrl();
		if (!url) return;

		navigator.clipboard.writeText(url).then(() => {
			message.success('链接已复制到剪贴板');
		});
	};

	return (
		<Card title="电商H5二维码生成">
			<Spin spinning={loading} tip="加载域名配置中...">
				<Space direction="vertical" size="large" style={{ width: '100%' }}>
					<div>
						<Text strong>环境选择：</Text>
						<Radio.Group
							value={env}
							onChange={(e) => setEnv(e.target.value)}
							options={ENV_OPTIONS}
							style={{ marginLeft: 8 }}
						/>
					</div>

					<Space wrap>
						<Text strong>选择页面：</Text>
						<Select
							placeholder="请选择H5页面"
							style={{ width: 200 }}
							options={H5_PAGES}
							value={selectedPage}
							onChange={setSelectedPage}
						/>
					</Space>

					{selectedPage && baseUrl && (
						<>
							<Space direction="vertical" size="middle">
								<Text type="secondary">
									页面链接：
									<Text copyable>{getFullUrl()}</Text>
								</Text>
							</Space>

							<Card style={{ width: 'fit-content' }}>
								<div
									ref={qrCodeRef}
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										background: '#fff',
										padding: '8px',
									}}
								>
									<QRCodeSVG
										value={getFullUrl()}
										size={200}
										bgColor="#ffffff"
										marginSize={0}
										fgColor="#000000"
										level="H"
									/>
									<Text
										style={{
											marginTop: '8px',
											fontSize: '14px',
											color: '#333',
										}}
									>
										{getPageName()} {getFormattedDate()}
									</Text>
								</div>
							</Card>

							<Space>
								<Button type="primary" onClick={handleDownload}>
									下载二维码
								</Button>
								<Button onClick={handleCopyUrl}>复制链接</Button>
							</Space>
						</>
					)}

					{selectedPage && !baseUrl && !loading && (
						<Text type="warning">未获取到域名配置，请检查接口</Text>
					)}
				</Space>
			</Spin>
		</Card>
	);
}
